import { Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { toDateString } from 'app/modules/main/helper/date-mapper';
import { SUCCESS } from 'app/modules/main/helper/observable-success';
import { ErrorMessage } from 'app/modules/main/models/authorization';
import { Access } from 'app/modules/main/models/authorization';
import { WindowService } from 'app/modules/main/services/window.service';
import { auth0authConfig } from 'app/oauth-config/auth0-oauth-config';
import { authConfig } from 'app/oauth-config/oauth-config';
import { CookieService } from 'ngx-cookie-service';
import { of, throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class OAuthCodeFlowService {
  private authConfig: AuthConfig;
  private provider: string;

  constructor(
    private readonly oAuthService: OAuthService,
    private readonly windowService: WindowService,
    private readonly cookieService: CookieService,
  ) {
    this.provider = this.cookieService.get('auth-provider');
    this.authConfig =
      this.provider === 'auth0'
        ? auth0authConfig
        : authConfig;
  }

  authorize(): Observable<Access> {
    return of(true)
      .pipe(
        map(() => this.grantAccess()),
        catchError(() => {
          this.refuseAccess();
          return throwError(() => new Error(ErrorMessage.NOT_AUTHENTICATED));
        })
      );
  }

  refresh(): Observable<Access> {

    return this.tryRefresh()
      .pipe(
        map(() => this.grantAccess()),
        catchError(() => {
          this.refuseAccess();
          return throwError(() => ErrorMessage.NOT_AUTHENTICATED);
        })
      );
  }

  private tryRefresh(): Observable<void> {

    return from(this.oAuthService.silentRefresh()).pipe(
        tap(() => {
          if (!this.oAuthService.hasValidAccessToken()) {
            throw ErrorMessage.NOT_AUTHENTICATED;
          }
        }),
        switchMap(() => SUCCESS),
      );
  }

  private grantAccess(): Access {
    const claims = this.oAuthService.getIdentityClaims();
    const userId = String(claims?.['https://awin.com/userId'] ?? claims?.['sub']);
    const scopes =
      Object.values(this.oAuthService.getGrantedScopes())
        .flatMap(scopes => String(scopes).split(' '))
        .map(scope => scope.trim())
        .filter(scope => !!scope);

    const token = this.oAuthService.getAccessToken();
    const expires = toDateString(new Date(this.oAuthService.getAccessTokenExpiration()));

    return { userId, scopes, token, expires };
  }

  private refuseAccess(): void {
    this.redirectToIdentityProvider();
  }

  private redirectToIdentityProvider(): void {

    if (!this.oAuthService.state) {
      this.makeFirstAuthAttempt();
    } else {
      this.makeSubsequentAuthAttempt();
    }
  }

  private makeFirstAuthAttempt() {

    const returnTo = this.windowService.locationUri + '?authAttempts=1';

    this.oAuthService.initCodeFlow(returnTo);
  }

  private makeSubsequentAuthAttempt() {

    const storedLocation = decodeURIComponent(this.oAuthService.state);
    const attemptsMatch = storedLocation.match(/[?&]authAttempts=(\d+)/);

    const attempt = attemptsMatch?.length == 2 ? parseInt(attemptsMatch[1]) : 0;

    if (attempt >= (this.authConfig as any)?.maxAuthAttempts) {

      console.warn('Authentication failed after 3 unsuccessful attempts');
      this.windowService.redirectToUnauthorizedPage();

      return;
    }

    const nextAttempt = attempt + 1;

    const returnTo = storedLocation.replace(`authAttempts=${attempt}`, `authAttempts=${nextAttempt}`);

    this.oAuthService.initCodeFlow(returnTo);
  }

}
